// src/components/principal/YourPhotosGrid.jsx (v5 – final, warning-free)

import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { API_PATHS, getAssetUrl } from "../../config";
import timeAgo from "../../utils/timeAgo";

const PAGE_SIZE = 20;

/**
 * Grid view of photos uploaded by the principal (or staff).
 *
 * Supports infinite scroll, video thumbnails, and inline story editing.
 *
 * @component
 * @param {Object} props
 * @param {function} props.onError - Error reporting callback
 * @returns {JSX.Element}
 */
const YourPhotosGrid = ({ onError }) => {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [story, setStory] = useState("");

  const observer = useRef(null);

  const lastPhotoRef = (node) => {
    if (observer.current) observer.current.disconnect();
    if (!node || !hasMore || loading) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.5 }
    );
    observer.current.observe(node);
  };

  // Load photos when page changes
  useEffect(() => {
    if (loading || !hasMore) return;

    const fetchPhotos = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(API_PATHS.MY_PHOTOS, {
          params: {
            my_photos: true,
            offset: page * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
        });

        const newPhotos = Array.isArray(data) ? data : [];
        setPhotos((prev) => (page === 0 ? newPhotos : [...prev, ...newPhotos]));
        setHasMore(newPhotos.length === PAGE_SIZE);
      } catch {
        onError?.("Failed to load your photos.");
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [page, hasMore, loading, onError]);

  const startEdit = (photo) => {
    setEditingId(photo.id);
    setStory(photo.story || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setStory("");
  };

  const saveStory = async (photoId) => {
    const trimmed = story.trim();
    if (!trimmed) {
      onError?.("Story cannot be empty.");
      return;
    }

    try {
      await api.post(`/photos/${photoId}/update-story`, {
        new_story: trimmed,
      });

      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, story: trimmed } : p))
      );
      setEditingId(null);
      setStory("");
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update story.");
    }
  };

  if (loading && photos.length === 0) {
    return (
      <div className="principal-panel photos-content">
        <p className="loading-indicator">Loading your photos…</p>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="principal-panel photos-content">
        <p>You haven’t uploaded any approved photos yet.</p>
      </div>
    );
  }

  return (
    <div className="principal-panel photos-content">
      <div className="photos-grid">
        {photos.map((photo, index) => {
          const isVideo = photo.media_type === "VIDEO";
          const durationSeconds =
            photo.video_duration_ms > 0
              ? Math.round(photo.video_duration_ms / 1000)
              : null;

          const mediaUrl = getAssetUrl(
            isVideo ? photo.file_path || photo.thumbnail_path : photo.thumbnail_path
          );

          return (
            <div
              key={photo.id}
              className="principal-item-card photo-card"
              ref={index === photos.length - 1 ? lastPhotoRef : null}
            >
              {isVideo ? (
                <div className="photo-video-wrapper">
                  <video
                    src={mediaUrl}
                    className="photo-thumbnail"
                    muted
                    playsInline
                    controls
                  />
                  <div className="photo-video-pill">
                    <span>▶ Video</span>
                    {durationSeconds !== null && (
                      <span className="photo-video-duration">{durationSeconds}s</span>
                    )}
                  </div>
                </div>
              ) : (
                <img
                  src={mediaUrl}
                  alt={photo.story || "Your photo"}
                  className="photo-thumbnail"
                  loading="lazy"
                />
              )}

              {editingId === photo.id ? (
                <div className="edit-story-form">
                  <textarea
                    value={story}
                    onChange={(e) => setStory(e.target.value)}
                    placeholder="Edit story and hashtags"
                    className="form-textarea"
                    maxLength={255}
                    rows={4}
                  />
                  <div className="edit-actions">
                    <button
                      onClick={() => saveStory(photo.id)}
                      className="save-story-button"
                    >
                      Save
                    </button>
                    <button onClick={cancelEdit} className="cancel-story-button">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="photo-story">
                    {photo.story || "No story added"}
                  </p>
                  <p className="photo-meta">
                    Uploaded {photo.created_at ? timeAgo(photo.created_at) : "N/A"}
                    {isVideo && (
                      <>
                        {" • "}Video
                        {durationSeconds !== null && ` · ${durationSeconds}s`}
                      </>
                    )}
                  </p>
                  <button
                    onClick={() => startEdit(photo)}
                    className="edit-story-button"
                  >
                    Edit Story
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {loading && <p className="loading-indicator">Loading more photos…</p>}
      {!hasMore && photos.length > 0 && (
        <p className="no-more-photos">No more photos to load.</p>
      )}
    </div>
  );
};

export default YourPhotosGrid;